import { useEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";
import { useSim } from "../../lib/store";

// Mobile LOD: fewer NPCs on small screens (phones/tablets) to hold frame rate.
const COUNT = typeof window !== "undefined" && window.innerWidth < 1024 ? 180 : 500;
const ROADS = [-42, -28, -14, 0, 14, 28, 42];
const SPAWN = [-35, -21, -7, 7, 21, 35];
const NPC_COLORS = ["#e0b089", "#c98a5e", "#f0d0b0", "#a9744f"];

function nearestRoadPoint(x, z) {
  // snap to nearest road centerline (whichever axis is closer)
  let bx = x, bz = z, best = Infinity;
  for (const r of ROADS) {
    if (Math.abs(x - r) < best) { best = Math.abs(x - r); bx = r; bz = z; }
    if (Math.abs(z - r) < best) { best = Math.abs(z - r); bx = x; bz = r; }
  }
  return new THREE.Vector3(bx, 0, bz);
}

// Low-poly crowd: idle wander -> panic run toward road centers during a quake.
export function CrowdAI() {
  const bodyRef = useRef();
  const headRef = useRef();
  const dummy = useMemo(() => new THREE.Object3D(), []);

  const npcs = useMemo(() => {
    const arr = [];
    for (let i = 0; i < COUNT; i++) {
      // spawn on sidewalks & parks across the metropolis
      const gx = SPAWN[Math.floor(Math.random() * SPAWN.length)] + (Math.random() - 0.5) * 10;
      const gz = SPAWN[Math.floor(Math.random() * SPAWN.length)] + (Math.random() - 0.5) * 10;
      const home = new THREE.Vector3(gx, 0, gz);
      arr.push({
        home,
        pos: home.clone(),
        road: nearestRoadPoint(gx, gz),
        phase: Math.random() * Math.PI * 2,
        speed: 2.4 + Math.random() * 2.6,
        wanderR: 0.8 + Math.random() * 1.4,
        color: new THREE.Color(NPC_COLORS[i % NPC_COLORS.length]),
      });
    }
    return arr;
  }, []);

  useEffect(() => {
    const body = bodyRef.current;
    if (!body) return;
    npcs.forEach((n, i) => body.setColorAt(i, n.color));
    if (body.instanceColor) body.instanceColor.needsUpdate = true;
  }, [npcs]);

  useFrame((state, delta) => {
    const st = useSim.getState();
    const body = bodyRef.current, head = headRef.current;
    if (!body || !head) return;
    const t = state.clock.elapsedTime;
    const dt = Math.min(delta, 0.05);
    const panic = st.simulating;
    const intensity = panic ? Math.sin(Math.min(st.simProgress, 1) * Math.PI) * 0.7 + 0.5 : 0;

    for (let i = 0; i < COUNT; i++) {
      const n = npcs[i];
      let bob;
      if (panic) {
        // run toward the road center
        const dir = n.road.clone().sub(n.pos);
        dir.y = 0;
        const d = dir.length();
        if (d > 0.05) dir.normalize();
        const run = n.speed * (1.6 + intensity) * dt;
        n.pos.addScaledVector(dir, Math.min(run, d));
        // scatter jitter
        n.pos.x += Math.sin(t * 10 + n.phase) * 0.02;
        bob = Math.abs(Math.sin(t * 12 + n.phase)) * 0.18;
      } else {
        // idle wander in a small circle around home
        const a = t * n.speed * 0.5 + n.phase;
        n.pos.x += ((n.home.x + Math.cos(a) * n.wanderR) - n.pos.x) * 0.04;
        n.pos.z += ((n.home.z + Math.sin(a) * n.wanderR) - n.pos.z) * 0.04;
        bob = Math.abs(Math.sin(t * 4 + n.phase)) * 0.06;
      }

      // body (capsule)
      dummy.position.set(n.pos.x, 0.28 + bob, n.pos.z);
      dummy.rotation.set(0, 0, 0);
      dummy.scale.set(1, 1, 1);
      dummy.updateMatrix();
      body.setMatrixAt(i, dummy.matrix);
      // head
      dummy.position.set(n.pos.x, 0.62 + bob, n.pos.z);
      dummy.scale.setScalar(1);
      dummy.updateMatrix();
      head.setMatrixAt(i, dummy.matrix);
    }
    body.instanceMatrix.needsUpdate = true;
    head.instanceMatrix.needsUpdate = true;
  });

  return (
    <group>
      <instancedMesh ref={bodyRef} args={[undefined, undefined, COUNT]}>
        <capsuleGeometry args={[0.09, 0.24, 4, 8]} />
        <meshStandardMaterial roughness={0.8} />
      </instancedMesh>
      <instancedMesh ref={headRef} args={[undefined, undefined, COUNT]}>
        <sphereGeometry args={[0.08, 8, 8]} />
        <meshStandardMaterial color="#f2c9a0" roughness={0.7} />
      </instancedMesh>
    </group>
  );
}
